const DEFAULT_RETRY_INTERVAL = 2000
const DEFAULT_RETRY_INTERVAL_BACKOFF_MULTIPLIER = 1.4

export enum EventStreamEventType {
  Connecting,
  Open,
  Error,
  Closed
}

export type EventSourceMessage = {
  event: string
  data: string
}

export function isEventSourceMessage(obj: EventStreamEventType | EventSourceMessage): obj is EventSourceMessage {
  return typeof obj === 'object' && (typeof obj.data === 'string' || typeof obj.event === 'string')
}

export function connectEventStream(
  url: string,
  getToken: () => Promise<string | undefined>,
  onMessage: (event: EventSourceMessage | EventStreamEventType) => void
) {
  const abortContoller = new AbortController()
  let connected
  const startInternal = async () => {
    let retryInterval = DEFAULT_RETRY_INTERVAL
    while (!abortContoller.signal.aborted) {
      const token = await getToken().catch(() => undefined)

      onMessage(EventStreamEventType.Connecting)

      await new Promise<void>((resolve) => {
        if (!token) {
          // no token, will atttempt to retry in rest of loop
          resolve()
          return
        }
        const eventSource = new EventSource(`${url}?access_token=${encodeURIComponent(token)}`)
        abortContoller.signal.onabort = () => {
          eventSource.close()
          connected = false
          resolve()
        }

        eventSource.addEventListener('message', (message) => {
          onMessage(message as unknown as EventSourceMessage)
        })
        eventSource.addEventListener('error', () => {
          eventSource.close()
          connected = false
          onMessage(EventStreamEventType.Error)
          retryInterval = retryInterval * DEFAULT_RETRY_INTERVAL_BACKOFF_MULTIPLIER
          resolve()
        })
        eventSource.addEventListener('open', () => {
          onMessage(EventStreamEventType.Open)
          connected = true
        })
      })

      if (abortContoller.signal.aborted) {
        return
      }

      await new Promise<void>((resolve) => {
        if (navigator.onLine) {
          setTimeout(resolve, retryInterval)
        } else {
          // if we're offline, stop trying ...
          retryInterval = DEFAULT_RETRY_INTERVAL
          const onOnline = () => {
            window.removeEventListener('online', onOnline)
            // ... and resume as soon as we're online
            resolve()
          }
          window.addEventListener('online', onOnline)
        }
      })
    }
  }
  startInternal()

  return {
    get connected() {
      return connected
    },
    unsubscribe: () => {
      abortContoller.abort()
    }
  }
}
